/**
 * Filtro de ruído aplicado na saída para o Sentry: descarta a rejeição de
 * play() que o próprio motor de mídia produz ao interromper um vídeo.
 *
 * ALCANCE: o predicado casa pela assinatura da rejeição, não pelo elemento que
 * a produziu — não há discriminador de componente nem de rota. Além do vídeo de
 * treino, o app tem um segundo play() sem dono, em qr-code.component.ts:160
 * (o <video> do leitor de QR, cujo stream é parado por stopScan()). Se esse
 * caminho rejeitar com a mesma assinatura, também é descartado. Aceite
 * conscientemente: é a mesma classe de ruído (promessa de play() interrompida
 * por teardown), e um discriminador por elemento exigiria carimbar contexto no
 * evento a partir de cada componente.
 *
 * O <video> da tela de treino usa os controles nativos do WebKit, então quem
 * chama play() é o motor e a promise devolvida não tem dono do lado JS. Fechar
 * a mídia executa pause() + load() de forma síncrona (necessário para o Android
 * não morrer com SIGBUS dentro do MediaCodec), e isso rejeita o play pendente
 * com AbortError — o mesmo vale para o pause() de quando o app vai para
 * background. Sem dono, a rejeição sobe como unhandledrejection.
 *
 * Sobrescrever video.play não resolve: os controles nativos chamam a
 * implementação interna do motor e nunca leem a propriedade trocada. Remover a
 * chamada síncrona também não, porque tirar um <video> tocando do documento
 * rejeita as promises pendentes do mesmo jeito. Enquanto os controles forem os
 * nativos, o código não tem como ser dono dessa promise.
 *
 * Nada quebra para o usuário — a mídia estava sendo fechada de propósito. O
 * custo é de cota e de ranking no Sentry, por isso o corte acontece aqui.
 *
 * O corte é estreito de propósito: cada condição existe para não engolir um
 * AbortError legítimo (um fetch cancelado por AbortController, por exemplo, é
 * rejeitado pelo WebKit com "Fetch is aborted"). Na dúvida, o evento é enviado.
 */

/** Código de DOMException para AbortError (DOMException.ABORT_ERR). */
const DOM_EXCEPTION_ABORT_ERR = 20;

/** Código de DOMException para InvalidStateError (DOMException.INVALID_STATE_ERR). */
const DOM_EXCEPTION_INVALID_STATE_ERR = 11;

/**
 * Mensagem exata do WebKit ao rejeitar o Picture-in-Picture automático que o
 * iOS tenta quando o app vai para background com o vídeo tocando inline. O
 * <video> tem `disablePictureInPicture`, então a chamada rejeita — e quem a
 * fez foi o motor, não o código, por isso a rejeição não tem dono (Sentry
 * TREINO-2E, 8 utilizadores na 3.22.0, release que já contém o wrapper de
 * requestPictureInPicture: envolver o método não intercepta a chamada interna,
 * exatamente como aconteceu com o play()).
 *
 * A comparação é pela mensagem EXATA, e isso é essencial: o TREINO-E é também
 * InvalidStateError com código 11, mas a mensagem é "The object is in an
 * invalid state." e continua por diagnosticar. Casar só por nome+código
 * engoliria os dois.
 */
const WEBKIT_PIP_NOT_READY_MESSAGE =
  'The video element is not ready to enter the Picture-in-Picture mode.';

/**
 * Mensagem exata que o WebKit usa ao rejeitar as promises de play() pendentes.
 * É o texto padrão do AbortError no motor, e é o que aparece nos eventos de iOS.
 * O Chromium escreve outra frase ("The play() request was interrupted by...");
 * ela não entra na lista enquanto não houver evento com esse texto.
 */
const WEBKIT_ABORT_MESSAGE = 'The operation was aborted.';

/** Recorte estrutural do evento: só o mecanismo é lido daqui. */
interface ISentryEventShape {
  exception?: {
    values?: {
      mechanism?: { type?: string };
    }[];
  };
}

/** Recorte estrutural do hint: `originalException` é o valor rejeitado cru. */
interface ISentryHintShape {
  originalException?: unknown;
}

/** Recorte estrutural do DOMException, lido sem depender do construtor global. */
interface IDomExceptionShape {
  name?: unknown;
  code?: unknown;
  message?: unknown;
}

/**
 * `true` quando o evento é a rejeição de play() descrita acima e pode ser
 * descartado. Qualquer condição não atendida devolve `false` — o padrão é
 * enviar.
 */
export function isMediaAbortRejection(event: ISentryEventShape, hint?: ISentryHintShape): boolean {
  // Só promises sem dono: um AbortError capturado de propósito pelo código
  // chega com mechanism 'generic' e continua sendo enviado.
  if (event.exception?.values?.[0]?.mechanism?.type !== 'onunhandledrejection') return false;

  const rejection = hint?.originalException as IDomExceptionShape | undefined | null;
  if (!rejection || typeof rejection !== 'object') return false;

  // AbortError de DOMException, não um Error qualquer com nome parecido.
  // Duas assinaturas conhecidas, ambas de promises que o motor de mídia cria e
  // que o código não tem como possuir. Cada uma casa nome + código + mensagem
  // exata; qualquer divergência devolve false e o evento é enviado.
  if (
    rejection.name === 'AbortError' &&
    rejection.code === DOM_EXCEPTION_ABORT_ERR &&
    rejection.message === WEBKIT_ABORT_MESSAGE
  ) {
    return true;
  }

  if (
    rejection.name === 'InvalidStateError' &&
    rejection.code === DOM_EXCEPTION_INVALID_STATE_ERR &&
    rejection.message === WEBKIT_PIP_NOT_READY_MESSAGE
  ) {
    return true;
  }

  return false;
}
